import React, { useState, useEffect } from 'react';
import { openDB } from 'idb';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

const AdminLegalEditor: React.FC = () => {
  const { mode } = useCustomTheme();
  const [legalContent, setLegalContent] = useState({
    compliance: '',
    intellectualProperty: '',
    disclaimers: '',
  });
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Load saved content on mount
  useEffect(() => {
    const loadContent = async () => {
      let loadedContent = null;
      
      // Try to load from multiple sources in order of preference
      const sources = [
        () => localStorage.getItem('goatCardsLegalContent'),
        () => sessionStorage.getItem('goatCardsLegalContent'),
        () => localStorage.getItem('goatCardsLegalContent_backup'),
        async () => {
          if ('indexedDB' in window) {
            try {
              const db = await openDB('goatCardsDB', 1);
              return await db.get('content', 'goatCardsLegalContent');
            } catch (error) {
              console.warn('IndexedDB load failed:', error);
              return null;
            }
          }
          return null;
        }
      ];
      
      for (const source of sources) {
        try {
          const result = await source();
          if (result) {
            const parsedContent = typeof result === 'string' ? JSON.parse(result) : result;
            if (parsedContent && typeof parsedContent === 'object') {
              loadedContent = parsedContent;
              console.log('Legal content loaded from source');
              break;
            }
          }
        } catch (error) {
          console.warn('Failed to load legal content from source:', error);
          continue;
        }
      }
      
      if (loadedContent) {
        setLegalContent(loadedContent);
        console.log('Legal content loaded successfully');
      }
    };
    
    loadContent();
  }, []);

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      // Save to multiple localStorage keys for redundancy
      localStorage.setItem('goatCardsLegalContent', JSON.stringify(legalContent));
      localStorage.setItem('goatCardsLegalContent_backup', JSON.stringify(legalContent));
      localStorage.setItem('goatCardsLegalContent_timestamp', new Date().toISOString());
      
      // Save to sessionStorage as additional backup
      sessionStorage.setItem('goatCardsLegalContent', JSON.stringify(legalContent));
      
      // Save to IndexedDB for persistent storage (if available)
      if ('indexedDB' in window) {
        try {
          const db = await openDB('goatCardsDB', 1, {
            upgrade(db) {
              if (!db.objectStoreNames.contains('content')) {
                db.createObjectStore('content');
              }
            },
          });
          await db.put('content', legalContent, 'goatCardsLegalContent');
        } catch (dbError) {
          console.warn('IndexedDB not available, using localStorage only:', dbError);
        }
      }
      
      console.log('Legal content saved to multiple storage locations');
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving legal content:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const isLightMode = mode === 'light';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: isLightMode ? '#000000' : '#FFFFFF' }}>
          Legal Information Editor
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ color: isLightMode ? '#666666' : '#a0aec0' }}>
          Edit the legal information that appears on the Legal page
        </Typography>
      </Box>

      {saveStatus === 'saved' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Legal information saved successfully!
        </Alert>
      )}

      {saveStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error saving legal information. Please try again.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Compliance Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Compliance Information
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Regulatory Compliance
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Compliance Details"
              value={legalContent.compliance}
              onChange={(e) => setLegalContent(prev => ({ ...prev, compliance: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Compliance Notes"
              value={legalContent.compliance}
              onChange={(e) => setLegalContent(prev => ({ ...prev, compliance: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
            />
          </Paper>
        </Grid>

        {/* Intellectual Property Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Intellectual Property
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Copyright & Trademarks
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="IP Information"
              value={legalContent.intellectualProperty}
              onChange={(e) => setLegalContent(prev => ({ ...prev, intellectualProperty: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Trademark Details"
              value={legalContent.intellectualProperty}
              onChange={(e) => setLegalContent(prev => ({ ...prev, intellectualProperty: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
            />
          </Paper>
        </Grid>

        {/* Disclaimers Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Disclaimers & Limitations
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Legal Disclaimers
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="General Disclaimers"
              value={legalContent.disclaimers}
              onChange={(e) => setLegalContent(prev => ({ ...prev, disclaimers: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Limitation of Liability"
              value={legalContent.disclaimers}
              onChange={(e) => setLegalContent(prev => ({ ...prev, disclaimers: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
            />
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          {saveStatus === 'saving' ? 'Saving...' : 'Save Legal Information'}
        </Button>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          Changes will be reflected on the Legal page immediately after saving.
        </Typography>
      </Box>
    </Container>
  );
};

export default AdminLegalEditor; 